import { useMemo } from 'react';
import { useAsync } from 'react-use';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  createScaffolderFieldExtension,
  type FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  BUILD_STATUS_ANNOTATION,
  IMAGE_FAMILY_ANNOTATION,
  INFRA_TYPE_ANNOTATION,
  INFRA_TYPE_IMAGE_BUILD,
  SOURCE_REPO_ANNOTATION,
} from '../catalog/catalogInfraTypes';

type ImageBuildOption = {
  ref: string;
  label: string;
  success: boolean;
};

const ImageBuildPicker = (
  props: FieldExtensionComponentProps<string | undefined>,
) => {
  const { onChange, value, rawErrors, formData } = props;
  const catalogApi = useApi(catalogApiRef);

  const { value: options = [], loading } = useAsync(async () => {
    const { items } = await catalogApi.getEntities({
      filter: { kind: 'Component' },
    });

    return items
      .filter(
        entity =>
          entity.metadata.annotations?.[INFRA_TYPE_ANNOTATION] ===
          INFRA_TYPE_IMAGE_BUILD,
      )
      .map(entity => {
        const family =
          entity.metadata.annotations?.[IMAGE_FAMILY_ANNOTATION] ?? '';
        const repo =
          entity.metadata.annotations?.[SOURCE_REPO_ANNOTATION] ?? '';
        const status =
          entity.metadata.annotations?.[BUILD_STATUS_ANNOTATION] ?? 'unknown';
        const imageVersion =
          entity.metadata.annotations?.['infrastructure.backstage.io/image-version'];
        const repoName = repo.split('/').pop() ?? entity.metadata.name;
        const success = status === 'success';
        const versionSuffix = imageVersion
          ? ` (${imageVersion})`
          : family
            ? ` — ${family.split('/').pop() ?? family}`
            : '';
        return {
          ref: stringifyEntityRef(entity),
          label: `${repoName}${versionSuffix}`,
          success,
        } satisfies ImageBuildOption;
      })
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
      );
  }, [catalogApi]);

  const selected = value ?? formData ?? '';

  const menuOptions = useMemo(
    () => options.filter(option => option.success),
    [options],
  );

  return (
    <FormControl variant="standard" fullWidth error={!!rawErrors?.length}>
      <InputLabel id="image-build-picker-label">Built image (catalog)</InputLabel>
      <Select
        labelId="image-build-picker-label"
        value={selected}
        onChange={event => {
          const next = event.target.value as string;
          onChange(next || undefined);
        }}
        disabled={loading}
      >
        <MenuItem value="">
          <em>None — use manual image family below</em>
        </MenuItem>
        {menuOptions.map(option => (
          <MenuItem key={option.ref} value={option.ref}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        Lists successful image builds from the catalog. Older or in-progress builds
        remain available via manual image family.
      </FormHelperText>
    </FormControl>
  );
};

export const ImageBuildPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ImageBuildPicker',
    component: ImageBuildPicker,
  }),
);
